/**
 * @file 学びメモの面 (いちごスタディのタブ「学びメモ」の中身) である。
 *
 * @what: Mac のメモと同じ3列にする。左の列で絞り込みを選ぶ。「すべて」の下に1段下げて本ごとの
 *   並び (本の小さな表紙・名前・件数。その本のメモを最後に作った順) を置き、そのあとに「本に紐づかないメモ」と
 *   各タグを並べる。中央の列にメモの一覧 (左に紐づく本の小さな表紙、題・日付・本文の先頭・本・タグ)、
 *   右の列に編集欄を置く。まなぶ人のメモだけを並べ、紐づけた
 *   本の名前から、その本を開ける。絞り込みは `?book=` / `?free=1` / `?tag=` のどれか1つ、開いている
 *   メモは `?note=` に持つ (`?view=notes` と並ぶ)。幅の狭い画面では、一覧と編集を切り替えて出す。
 *   幅の広い画面では、左の2列を閉じて編集欄を全幅にでき、閉じたかどうかは端末ごとに覚える。
 *   全画面にすると、ヘッダーやタブを隠して学びメモだけを画面いっぱいに出す (覚えない)。
 *   一覧の開閉と全画面のボタンは、文字を減らすため印だけにする (代表の指示、2026-11-02)。
 *   一覧の並び (更新が新しい順 / 作成が新しい順 / 題名順) は一覧の見出しの印から選び、端末ごとに覚える。
 * @why: 本を開かずにメモを書き、テーマ (タグ) ごと・本ごとに読み返す場所が無かった。本に紐づかない
 *   調べもののメモで運用し、タグで別々のメモを1つのテーマに繋ぐ (代表の判断、2026-10-29、
 *   plan/docs/study_learning_notes_design.md)。配置と、本だななどと同じタブに並べることは代表が選んだ。
 * @failure: 一覧の取得失敗は1行で知らせる。保存と削除の失敗は編集欄が知らせる
 *   (hooks/useStudyNoteEditor.ts)。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { FamilyMember } from "../api/members";
import { fetchStudyNotes, newStudyNoteId, type StudyNote, type StudyNoteRef } from "../api/notes";
import { NOTE_LIMIT_MESSAGE, useStudyNoteEditor } from "../hooks/useStudyNoteEditor";
import { useStudySources } from "../hooks/useStudySources";
import {
  formatNoteDate,
  noteBookIds,
  noteCoverSource,
  notePreview,
  sortNotes,
  summarizeNoteBooks,
  type NoteSort,
} from "../model/noteList";
import { summarizeNoteTags } from "../model/noteTags";
import { printedPageOf } from "../model/sourcePages";
import { notesForLearner } from "../model/studyNoteContext";
import { NoteSortMenu } from "./NoteSortMenu";
import { SourceCover } from "./SourceCover";
import { StudyNoteEditor } from "./StudyNoteEditor";
import "./study.css";

const LIST_CLOSED_KEY = "bb.study.notebook.listClosed";

/** 覚えている「一覧を閉じる」の値 (覚えていなければ開いている)。 */
function readListClosed(): boolean {
  try {
    return window.localStorage.getItem(LIST_CLOSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** 「一覧を閉じる」の値を覚える。 */
function writeListClosed(closed: boolean): void {
  try {
    if (closed) window.localStorage.setItem(LIST_CLOSED_KEY, "1");
    else window.localStorage.removeItem(LIST_CLOSED_KEY);
  } catch {
    // 覚えられなくても、その画面のあいだは切り替えが効く
  }
}

const SORT_KEY = "bb.study.notebook.sort";

/** 覚えている一覧の並び (覚えていなければ更新が新しい順)。 */
function readSort(): NoteSort {
  try {
    const v = window.localStorage.getItem(SORT_KEY);
    return v === "created" || v === "title" ? v : "updated";
  } catch {
    return "updated";
  }
}

/** 一覧の並びを覚える。 */
function writeSort(sort: NoteSort): void {
  try {
    if (sort === "updated") window.localStorage.removeItem(SORT_KEY);
    else window.localStorage.setItem(SORT_KEY, sort);
  } catch {
    // 覚えられなくても、その画面のあいだは切り替えが効く
  }
}

const iconProps = {
  className: "study-notebook-icon",
  viewBox: "0 0 16 16",
  "aria-hidden": true,
  focusable: "false" as const,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** 一覧を閉じる (左向き) / 開く (右向き) の印である。 */
function ListToggleIcon({ closed }: { closed: boolean }) {
  return (
    <svg {...iconProps}>
      <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.5" />
      <path d="M6 2.75v10.5" />
      <path d={closed ? "M9 6l2 2-2 2" : "M11 6L9 8l2 2"} />
    </svg>
  );
}

/** 全画面にする (四隅が外向き) / やめる (内向き) の印である。 */
function FullscreenIcon({ on }: { on: boolean }) {
  return (
    <svg {...iconProps}>
      <path
        d={on ? "M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" : "M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"}
      />
    </svg>
  );
}

type NotesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; notes: StudyNote[] };

/** 絞り込みである。URL の `?book=` / `?free=1` / `?tag=` と1対1に対応する。 */
type NoteFilter =
  | { kind: "all" }
  | { kind: "book"; bookId: string }
  | { kind: "free" }
  | { kind: "tag"; tag: string };

function filterOf(params: URLSearchParams): NoteFilter {
  const book = params.get("book");
  if (book !== null) return { kind: "book", bookId: book };
  const tag = params.get("tag");
  if (tag !== null) return { kind: "tag", tag };
  if (params.get("free") === "1") return { kind: "free" };
  return { kind: "all" };
}

export function StudyNotebook({
  familyId,
  learnerId,
  learners,
}: {
  familyId: string;
  /** 画面で選んでいるまなぶ人である (ヘッダーで切り替える)。 */
  learnerId: string | null;
  /** 家族のまなぶ人 (2人以上のとき、メモの付け替えの選択肢に出す)。 */
  learners: readonly FamilyMember[];
}) {
  const [params, setParams] = useSearchParams();
  const sourcesState = useStudySources(familyId);
  const sources = useMemo(
    () => (sourcesState.status === "ready" ? sourcesState.sources : []),
    [sourcesState],
  );
  const sourcesById = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources]);

  const [notesState, setNotesState] = useState<NotesState>({ status: "loading" });
  const [listClosed, setListClosed] = useState(readListClosed);
  const [sort, setSort] = useState(readSort);
  const changeSort = (next: NoteSort) => {
    setSort(next);
    writeSort(next);
  };
  const [fullscreen, setFullscreen] = useState(false);
  // 全画面の間は後ろの画面を動かさず、Esc で戻す。※ただし変換中の Esc (変換の取り消し) と、
  //   タグの入力欄の Esc (入力のやめ) では戻さない。削除の確認の対話は自分で Esc を止める。
  useEffect(() => {
    if (!fullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.isComposing) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);
  const toggleList = () => {
    setListClosed((prev) => {
      writeListClosed(!prev);
      return !prev;
    });
  };
  useEffect(() => {
    const controller = new AbortController();
    setNotesState({ status: "loading" });
    fetchStudyNotes(familyId, controller.signal)
      .then((notes) => {
        if (!controller.signal.aborted) setNotesState({ status: "ready", notes });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load notes", err);
        setNotesState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId]);

  const applySaved = useCallback((saved: StudyNote) => {
    setNotesState((prev) =>
      prev.status === "ready"
        ? { ...prev, notes: [saved, ...prev.notes.filter((n) => n.id !== saved.id)] }
        : prev,
    );
  }, []);
  const dropNote = useCallback(
    (noteId: string) => {
      setNotesState((prev) =>
        prev.status === "ready"
          ? { ...prev, notes: prev.notes.filter((n) => n.id !== noteId) }
          : prev,
      );
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get("note") === noteId) next.delete("note");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const editor = useStudyNoteEditor({
    familyId,
    learnerPersonId: learnerId,
    onSaved: applySaved,
    onRemoved: dropNote,
    onDiscarded: dropNote,
    rememberBodyView: true,
  });
  const { selectedId, openNote, closeNote, saveIfDirty } = editor;

  // まなぶ人のメモ (学習者を持たない古いメモも、本を読む画面と同じく出す)。
  const notes = useMemo(
    () => (notesState.status === "ready" ? notesForLearner(notesState.notes, learnerId) : []),
    [notesState, learnerId],
  );
  const tags = useMemo(() => summarizeNoteTags(notes), [notes]);
  const books = useMemo(() => summarizeNoteBooks(notes, sourcesById), [notes, sourcesById]);
  const freeCount = useMemo(() => notes.filter((n) => (n.refs ?? []).length === 0).length, [notes]);

  const filter = filterOf(params);
  // 件数は多くて数百で、絞り込みは描画のたびに作り直しても軽い
  const shown = sortNotes(notes, sort).filter((n) => {
    switch (filter.kind) {
      case "book":
        return noteBookIds(n, sourcesById).has(filter.bookId);
      case "free":
        return (n.refs ?? []).length === 0;
      case "tag":
        return (n.tags ?? []).includes(filter.tag);
      default:
        return true;
    }
  });

  const setFilter = (next: NoteFilter) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("book");
        p.delete("tag");
        p.delete("free");
        if (next.kind === "book") p.set("book", next.bookId);
        else if (next.kind === "tag") p.set("tag", next.tag);
        else if (next.kind === "free") p.set("free", "1");
        return p;
      },
      { replace: true },
    );
  };

  // `?note=` のメモを開く (本だなの「さいきん書いたメモ」から来たとき・開き直したとき)。
  // まなぶ人のメモでなければ開かない。
  const noteParam = params.get("note");
  useEffect(() => {
    if (notesState.status !== "ready") return;
    if (noteParam === null) {
      if (selectedId !== null) closeNote();
      return;
    }
    if (noteParam === selectedId) return;
    const target = notes.find((n) => n.id === noteParam);
    if (target === undefined) return;
    void (async () => {
      // 書きかけがあれば保存してから切り替える。保存できなければ切り替えない。
      if (!(await saveIfDirty())) return;
      openNote(target);
    })();
  }, [notesState.status, noteParam, notes, selectedId, openNote, closeNote, saveIfDirty]);

  // まなぶ人を切り替えて、開いているメモがその人のものでなくなったら閉じる。
  useEffect(() => {
    if (selectedId === null || notesState.status !== "ready") return;
    if (notes.some((n) => n.id === selectedId)) return;
    void (async () => {
      await saveIfDirty();
      closeNote();
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete("note");
          return p;
        },
        { replace: true },
      );
    })();
  }, [learnerId, notes, selectedId, notesState.status, saveIfDirty, closeNote, setParams]);

  const selectNote = (noteId: string | null) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (noteId === null) p.delete("note");
        else p.set("note", noteId);
        return p;
      },
      // 一覧と編集の切り替えをブラウザの「戻る」で戻せるように、開くときは履歴に積む
      { replace: noteId === null },
    );
  };

  const createNote = async () => {
    if (learnerId === null) return;
    if (!(await saveIfDirty())) return;
    const now = new Date().toISOString();
    // 絞り込みの中に新しいメモが出るように、本で絞っていればその本に紐づけ、タグで絞っていれば
    // そのタグを付けて作る
    const note: StudyNote = {
      id: newStudyNoteId(),
      learner_person_id: learnerId,
      title: "新しいメモ",
      ...(filter.kind === "book" ? { refs: [{ kind: "source", source_id: filter.bookId }] } : {}),
      ...(filter.kind === "tag" ? { tags: [filter.tag] } : {}),
      created_at: now,
      updated_at: now,
    };
    // 先に `?note=` を付ける。※なぜなら付ける前に開くと、`?note=` の無い状態を「閉じた」と読んで
    //   開いたばかりのメモを閉じるためである。件数の上限で断られたら dropNote が外す。
    selectNote(note.id);
    await editor.createNote(note);
  };

  const refLabel = (ref: StudyNoteRef): string => {
    const target = sourcesById.get(ref.source_id);
    const title = target?.title ?? ref.source_id;
    if (ref.page === undefined || ref.page <= 0) return title;
    return `${title} p.${target ? printedPageOf(target, ref.page) : ref.page}`;
  };
  const refHref = (ref: StudyNoteRef): string =>
    `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(ref.source_id)}${
      ref.page !== undefined && ref.page > 0 ? `?page=${ref.page}` : ""
    }`;

  if (learnerId === null) {
    return <p className="study-muted">まなぶ人を選ぶと、メモが並びます。</p>;
  }
  if (notesState.status === "loading") {
    return (
      <p className="study-muted" role="status">
        メモを読み込んでいます...
      </p>
    );
  }
  if (notesState.status === "error") {
    return (
      <p className="study-error" role="alert">
        メモを読み込めませんでした。時間を置いて開き直してください。
      </p>
    );
  }

  const editing = selectedId !== null && editor.draft !== null;
  const filterLabel =
    filter.kind === "book"
      ? (sourcesById.get(filter.bookId)?.title ?? filter.bookId)
      : filter.kind === "tag"
        ? `#${filter.tag}`
        : filter.kind === "free"
          ? "本に紐づかないメモ"
          : "すべて";

  return (
    <div
      className={`study-notebook${editing ? " is-editing" : ""}${listClosed ? " is-list-closed" : ""}${
        fullscreen ? " is-fullscreen" : ""
      }`}
    >
      <nav className="study-notebook-tags" aria-label="メモをしぼりこむ">
        <button
          type="button"
          className="study-notebook-tag"
          aria-pressed={filter.kind === "all"}
          onClick={() => setFilter({ kind: "all" })}
        >
          <span className="study-notebook-tag-name">すべて</span>
          <span className="study-notebook-tag-count">{notes.length}</span>
        </button>
        {books.map((b) => {
          const source = sourcesById.get(b.bookId);
          return (
            <button
              key={`book-${b.bookId}`}
              type="button"
              className="study-notebook-tag study-notebook-tag--book"
              aria-pressed={filter.kind === "book" && filter.bookId === b.bookId}
              onClick={() => setFilter({ kind: "book", bookId: b.bookId })}
            >
              {/* 本だなから消えた本は、表紙の代わりに空の枠を出して名前の位置をそろえる */}
              {source ? (
                <SourceCover
                  familyId={familyId}
                  source={source}
                  className="study-notebook-book-cover"
                />
              ) : (
                <span
                  className="study-notebook-book-cover study-notebook-item-cover--none"
                  aria-hidden="true"
                />
              )}
              <span className="study-notebook-tag-name">{b.title}</span>
              <span className="study-notebook-tag-count">{b.count}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="study-notebook-tag"
          aria-pressed={filter.kind === "free"}
          onClick={() => setFilter({ kind: "free" })}
        >
          <span className="study-notebook-tag-name">本に紐づかないメモ</span>
          <span className="study-notebook-tag-count">{freeCount}</span>
        </button>
        {tags.map((t) => (
          <button
            key={`tag-${t.tag}`}
            type="button"
            className="study-notebook-tag"
            aria-pressed={filter.kind === "tag" && filter.tag === t.tag}
            onClick={() => setFilter({ kind: "tag", tag: t.tag })}
          >
            <span className="study-notebook-tag-name">#{t.tag}</span>
            <span className="study-notebook-tag-count">{t.count}</span>
          </button>
        ))}
      </nav>

      <div className="study-notebook-list">
        <div className="study-notebook-list-head">
          <span className="study-notebook-list-title">{filterLabel}</span>
          {/* 幅の狭い画面で全画面のまま一覧へ戻ったときの、全画面のやめ口 (編集欄の上の並びが隠れるため) */}
          {fullscreen ? (
            <button
              type="button"
              className="study-notebook-icon-btn study-notebook-list-fullscreen-exit"
              aria-label="全画面をやめる"
              title="全画面をやめる"
              onClick={() => setFullscreen(false)}
            >
              <FullscreenIcon on />
            </button>
          ) : null}
          <NoteSortMenu sort={sort} onChange={changeSort} />
          <button
            type="button"
            className="study-note-btn study-note-btn--primary"
            onClick={() => void createNote()}
          >
            新しいメモ
          </button>
        </div>
        {editor.limitReached && !editing ? (
          <p role="alert" className="study-note-muted">
            {NOTE_LIMIT_MESSAGE}
          </p>
        ) : null}
        {shown.length === 0 ? (
          <p className="study-note-muted">
            {notes.length === 0
              ? "まだメモがありません。「新しいメモ」で書きはじめられます。"
              : "ここに入るメモはありません。"}
          </p>
        ) : (
          <ul className="study-notebook-items" aria-label="メモの一覧">
            {shown.map((n) => {
              const cover = noteCoverSource(n, sourcesById);
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`study-notebook-item${n.id === selectedId ? " is-active" : ""}`}
                    aria-current={n.id === selectedId ? "true" : undefined}
                    onClick={() => selectNote(n.id)}
                  >
                    {/* 本に紐づかないメモは空の枠にして、行の文字の位置をそろえる */}
                    {cover ? (
                      <SourceCover
                        familyId={familyId}
                        source={cover.source}
                        {...(cover.parent ? { parent: cover.parent } : {})}
                        className="study-notebook-item-cover"
                      />
                    ) : (
                      <span
                        className="study-notebook-item-cover study-notebook-item-cover--none"
                        aria-hidden="true"
                      />
                    )}
                    <span className="study-notebook-item-text">
                      <span className="study-notebook-item-title">{n.title}</span>
                      <span className="study-notebook-item-line">
                        <span className="study-notebook-item-date">
                          {formatNoteDate(sort === "created" ? n.created_at : n.updated_at)}
                        </span>
                        <span className="study-notebook-item-preview">{notePreview(n.body)}</span>
                      </span>
                      {(n.tags ?? []).length > 0 || (n.refs ?? []).length > 0 ? (
                        <span className="study-notebook-item-meta">
                          {(n.refs ?? []).length > 0 ? (
                            <span>{refLabel((n.refs ?? [])[0]!)}</span>
                          ) : null}
                          {(n.tags ?? []).map((t) => (
                            <span key={t}>#{t}</span>
                          ))}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="study-notebook-editor">
        <div className="study-notebook-editor-bar">
          <button
            type="button"
            className="study-notebook-icon-btn study-notebook-list-toggle"
            aria-label={listClosed ? "一覧を開く" : "一覧を閉じる"}
            title={listClosed ? "一覧を開く" : "一覧を閉じる"}
            aria-expanded={!listClosed}
            onClick={toggleList}
          >
            <ListToggleIcon closed={listClosed} />
          </button>
          {editing ? (
            <button
              type="button"
              className="study-ghost-btn study-notebook-back"
              onClick={() => {
                void (async () => {
                  if (await saveIfDirty()) selectNote(null);
                })();
              }}
            >
              ← 一覧
            </button>
          ) : null}
          <button
            type="button"
            className="study-notebook-icon-btn study-notebook-fullscreen-toggle"
            aria-label={fullscreen ? "全画面をやめる" : "全画面にする"}
            title={fullscreen ? "全画面をやめる" : "全画面にする"}
            aria-pressed={fullscreen}
            onClick={() => setFullscreen((v) => !v)}
          >
            <FullscreenIcon on={fullscreen} />
          </button>
        </div>
        {editing ? (
          <>
            <StudyNoteEditor
              editor={editor}
              learners={learners}
              learnerPersonId={learnerId}
              sources={sources}
              refLabel={refLabel}
              refHref={refHref}
              tagSuggestions={tags.map((t) => t.tag)}
              allowSplit
            />
          </>
        ) : (
          <p className="study-note-muted">一覧からメモを選ぶか、「新しいメモ」で書きはじめます。</p>
        )}
      </div>
    </div>
  );
}
